"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ToggleButton = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _Button = require("../Button/Button");
var _excluded = ["pressed", "defaultPressed", "onClick"];
/**
 * Toggle buttons are an official
 * [WAI-ARIA button](https://www.w3.org/TR/wai-aria-practices/#button) type that use the
 * [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) attribute to tell assistive
 * technologies that the button is a toggle button.
 *
 * **Important:** it is critical the label on a toggle does not change when its state changes.
 *
 * @extends Button
 * @since 8.0.0
 */
var ToggleButton = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var pressed = _ref.pressed,
    defaultPressed = _ref.defaultPressed,
    onClick = _ref.onClick,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var _useState = (0, _react.useState)(defaultPressed),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    pressedState = _useState2[0],
    setPressedState = _useState2[1];
  var onClickCallback = (0, _react.useCallback)(function (e) {
    setPressedState(!pressedState);
    if (onClick) {
      onClick(e, typeof pressed !== 'undefined' ? pressed : !pressedState);
    }
  }, [onClick, pressed, pressedState]);

  // If pressed is defined, use that value as a controlled value, otherwise use local
  // uncontrolled state.
  var ariaPressed = pressedState;
  if (typeof pressed !== 'undefined') {
    ariaPressed = pressed;
  }
  return /*#__PURE__*/_react.default.createElement(_Button.Button, (0, _extends2.default)({
    "aria-pressed": !!ariaPressed,
    onClick: onClickCallback,
    ref: forwardedRef
  }, rest));
});
exports.ToggleButton = ToggleButton;
ToggleButton.displayName = 'ToggleButton';
ToggleButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Change the type of the toggle button. All button types support a pressed state, but it is
   * recommended that you use the default "tertiary" type when using toggle buttons.
   */
  buttonType: _propTypes.default.oneOf(Object.values(_constants.BUTTON_TYPES)),
  /**
   * Sets the [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) state and use as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * A toggle button can use `pressed` or `defaultPressed`, but not both.
   */
  defaultPressed: _propTypes.default.bool,
  /**
   * Set the button as disabled.
   */
  disabled: _propTypes.default.bool,
  /**
   * Add an `Icon` to the button.
   */
  icon: _propTypes.default.node,
  /**
   * A click handler that will be called with two arguments, `e`, the button click event,
   * and `pressed`, the current pressed state.
   */
  onClick: _propTypes.default.func,
  /**
   * Sets the [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) state and use as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * A toggle button can use `pressed` or `defaultPressed`, but not both.
   */
  pressed: _propTypes.default.bool,
  /**
   * Change the button size. It is recommended that you use the default "sm" size when using
   * toggle buttons.
   */
  size: _propTypes.default.oneOf(Object.values(_constants.BUTTON_SIZES))
} : {};
ToggleButton.defaultProps = {
  buttonType: _constants.BUTTON_TYPES.tertiary,
  defaultPressed: undefined,
  disabled: false,
  icon: null,
  onClick: undefined,
  pressed: undefined,
  size: _constants.BUTTON_SIZES.sm
};